/**
 * Per-location inventory hydration via App Proxy (/apps/inventory).
 * One batched request per page; inStock = qty === null || qty > 0.
 */
(function () {
  const config = window.FoxTheme?.locationInventoryConfig;
  if (!config?.enabled) return;

  const MAX_GET_URL_LENGTH = 1800;
  const stock = Object.create(null);
  let fetchPromise = null;
  let pendingIds = new Set();
  let observer = null;
  let cartOverstockClamped = false;

  function inStock(qty) {
    return qty === null || qty === undefined || qty > 0;
  }

  function getQty(variantId) {
    if (variantId == null || variantId === '') return undefined;
    const key = String(variantId);
    return Object.prototype.hasOwnProperty.call(stock, key) ? stock[key] : undefined;
  }

  function isTracked(el) {
    const management = el.dataset.locInvManagement;
    return management === 'shopify';
  }

  function allowsContinue(el) {
    return el.dataset.locInvPolicy === 'continue';
  }

  function collectVariantIds(root) {
    const ids = new Set();
    root.querySelectorAll('[data-loc-inv-variant]').forEach((el) => {
      const id = el.dataset.locInvVariant;
      if (id) ids.add(String(id));
    });
    root.querySelectorAll('[data-loc-inv-variants]').forEach((el) => {
      (el.dataset.locInvVariants || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
        .forEach((id) => ids.add(id));
    });
    return [...ids];
  }

  function idsNeedingFetch(ids) {
    return ids.filter((id) => !Object.prototype.hasOwnProperty.call(stock, id));
  }

  async function fetchStock(variantIds) {
    if (!variantIds.length) return {};

    const { endpoint, market } = config;
    const query = `variant_ids=${encodeURIComponent(variantIds.join(','))}&market=${encodeURIComponent(market)}`;
    const getUrl = `${endpoint}?${query}`;

    let response;
    if (getUrl.length > MAX_GET_URL_LENGTH || variantIds.length > 800) {
      response = await fetch(`${endpoint}?market=${encodeURIComponent(market)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ variant_ids: variantIds.map((id) => Number(id)) }),
      });
    } else {
      response = await fetch(getUrl, { headers: { Accept: 'application/json' } });
    }

    if (!response.ok) throw new Error(`Inventory request failed (${response.status})`);
    return response.json();
  }

  function mergeStock(data) {
    Object.keys(data || {}).forEach((key) => {
      stock[String(key)] = data[key];
    });
  }

  async function ensureStock(root) {
    const missing = idsNeedingFetch(collectVariantIds(root));
    if (!missing.length) return true;

    missing.forEach((id) => pendingIds.add(id));

    if (!fetchPromise) {
      fetchPromise = (async () => {
        const batch = [...pendingIds];
        pendingIds.clear();
        try {
          const data = await fetchStock(batch);
          mergeStock(data);
          document.documentElement.classList.remove('loc-inv--fallback');
          document.documentElement.classList.add('loc-inv--ready');
          window.FoxTheme.locationInventory.stock = stock;
          document.dispatchEvent(
            new CustomEvent('location-inventory:ready', { detail: { stock } })
          );
          return true;
        } catch (error) {
          console.warn('[location-inventory]', error);
          document.documentElement.classList.add('loc-inv--fallback');
          return false;
        } finally {
          fetchPromise = null;
        }
      })();
    }

    await fetchPromise;

    const stillMissing = idsNeedingFetch(missing);
    if (stillMissing.length && !document.documentElement.classList.contains('loc-inv--fallback')) {
      try {
        const data = await fetchStock(stillMissing);
        mergeStock(data);
      } catch (error) {
        console.warn('[location-inventory]', error);
        document.documentElement.classList.add('loc-inv--fallback');
        return false;
      }
    }

    return !document.documentElement.classList.contains('loc-inv--fallback');
  }

  function resolveInventoryQty(el) {
    const variantIds = (el.dataset.locInvVariants || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    const mode = el.dataset.locInvMode || 'variant';

    if (variantIds.length > 1 && mode === 'product') {
      let allResolved = true;
      let anyInStock = false;

      for (const id of variantIds) {
        const quantity = getQty(id);
        if (quantity === undefined) {
          allResolved = false;
          break;
        }
        if (inStock(quantity)) anyInStock = true;
      }

      if (!allResolved) return undefined;
      return anyInStock ? null : 0;
    }

    const variantId = el.dataset.locInvVariant;
    return variantId ? getQty(variantId) : undefined;
  }

  function syncInventoryVariant(variant) {
    if (!variant?.id) return;

    const variantId = String(variant.id);

    document
      .querySelectorAll('[data-loc-inv-type="inventory-display"][data-loc-inv-variants]')
      .forEach((el) => {
        el.dataset.locInvVariant = variantId;
        el.dataset.locInvMode = 'variant';
      });

    document.querySelectorAll('[data-loc-inv-type="badge"][data-loc-inv-variant]').forEach((el) => {
      el.dataset.locInvVariant = variantId;
      if (variant.inventory_management != null) {
        el.dataset.locInvManagement = variant.inventory_management;
      }
      if (variant.inventory_policy != null) {
        el.dataset.locInvPolicy = variant.inventory_policy;
      }
    });

    document
      .querySelectorAll(
        '[data-loc-inv-type="buy-form"] .product-variant-id, [data-loc-inv-type="buy-form"] [data-loc-inv-variant]'
      )
      .forEach((el) => {
        el.dataset.locInvVariant = variantId;
        if (variant.inventory_management != null) {
          el.dataset.locInvManagement = variant.inventory_management;
        }
        if (variant.inventory_policy != null) {
          el.dataset.locInvPolicy = variant.inventory_policy;
        }
        if (variant.quantity_rule?.min != null) {
          el.dataset.locInvMin = String(variant.quantity_rule.min);
        }
      });
  }

  function productCardSoldOutAtLocation(variantIds) {
    let allResolved = true;
    let anyInStock = false;

    for (const id of variantIds) {
      const qty = getQty(id);
      if (qty === undefined) {
        allResolved = false;
        continue;
      }
      if (inStock(qty)) anyInStock = true;
    }

    if (!allResolved) return undefined;
    return !anyInStock;
  }

  function buyFormQuantityCap(qtyInput, qty) {
    const ruleMax = qtyInput.dataset.locInvRuleMax
      ? Number(qtyInput.dataset.locInvRuleMax)
      : null;
    const locationQty = Math.max(0, qty);
    return ruleMax != null && !Number.isNaN(ruleMax)
      ? Math.min(ruleMax, locationQty)
      : locationQty;
  }

  function setBuyFormExhausted(buyFormContainer, exhausted) {
    if (!buyFormContainer) return;
    buyFormContainer.dataset.locInvExhausted = exhausted ? 'true' : 'false';
  }

  function clearBuyFormQuantityMax(qtyInput) {
    qtyInput.removeAttribute('max');
    delete qtyInput.dataset.max;
  }

  function applyBuyFormQuantityCap(scope, buyFormContainer, variantInput, qty) {
    const qtyInput = scope.querySelector('[data-loc-inv-qty-input]');
    if (!qtyInput || !isTracked(variantInput) || allowsContinue(variantInput)) {
      setBuyFormExhausted(buyFormContainer, false);
      return false;
    }

    if (typeof qty !== 'number') {
      setBuyFormExhausted(buyFormContainer, false);
      return false;
    }

    const cartQty = Number(qtyInput.dataset.cartQuantity || 0);
    const cap = buyFormQuantityCap(qtyInput, Math.max(0, qty - cartQty));
    const min = Number(qtyInput.dataset.min || qtyInput.min || 1);

    if (cap < min) {
      clearBuyFormQuantityMax(qtyInput);
      const currentValue = parseInt(qtyInput.value, 10);
      if (Number.isNaN(currentValue) || currentValue < min) {
        qtyInput.value = String(min);
      }
      setBuyFormExhausted(buyFormContainer, true);
      return true;
    }

    setBuyFormExhausted(buyFormContainer, false);
    qtyInput.max = cap;
    qtyInput.dataset.max = String(cap);

    const currentValue = parseInt(qtyInput.value, 10);
    if (!Number.isNaN(currentValue) && currentValue > cap) {
      qtyInput.value = String(cap);
    }

    return true;
  }

  function showBuyFormError(form, message) {
    const errorMessage = form.querySelector('.product-form__error-message');
    const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
    if (errorMessage) {
      errorMessage.removeAttribute('hidden');
      errorMessage.innerText = message;
    }
    if (errorWrapper) {
      errorWrapper.removeAttribute('hidden');
    }
  }

  function formatInventoryMessage(el, qty) {
    const strings = config.strings || {};
    const showCount = el.dataset.locInvShowCount === 'true';
    const threshold = Number(el.dataset.locInvThreshold) || 10;

    if (!inStock(qty)) {
      if (allowsContinue(el)) return strings.continueSelling || '';
      return strings.outOfStock || '';
    }

    if (qty === null || qty === undefined) {
      return strings.inStock || '';
    }

    if (qty <= threshold) {
      if (showCount) {
        return qty === 1
          ? (strings.lowStockOne || '').replace('[quantity]', qty)
          : (strings.lowStockMany || '').replace('[quantity]', qty);
      }
      return strings.lowStock || '';
    }

    if (showCount) {
      return (strings.inStockCount || '').replace('[quantity]', qty);
    }
    return strings.inStock || '';
  }

  function setSoldOutState(container, soldOut) {
    const soldOutEl = container.querySelector('[data-loc-inv-sold-out]');
    const availableEl = container.querySelector('[data-loc-inv-available]');
    if (soldOutEl) {
      soldOutEl.hidden = !soldOut;
      soldOutEl.classList.toggle('hidden', !soldOut);
    }
    if (availableEl) {
      availableEl.hidden = soldOut;
      availableEl.classList.toggle('hidden', soldOut);
    }
  }

  function applyInventoryDisplay(root) {
    root.querySelectorAll('[data-loc-inv-type="inventory-display"]').forEach((el) => {
      if (!isTracked(el)) return;

      const qty = resolveInventoryQty(el);
      if (qty === undefined) return;

      const textEl = el.querySelector('[data-loc-inv-text]');
      const barEl = el.querySelector('progress-bar[data-loc-inv-bar]');
      const threshold = Number(el.dataset.locInvThreshold) || 10;
      const available = inStock(qty);
      const message = formatInventoryMessage(el, qty);

      el.classList.remove(
        'product__inventory--in-stock',
        'product__inventory--low-stock',
        'product__inventory--out-of-stock'
      );

      if (!available) {
        el.classList.add('product__inventory--out-of-stock');
      } else if (typeof qty === 'number' && qty > 0 && qty <= threshold) {
        el.classList.add('product__inventory--low-stock');
      } else {
        el.classList.add('product__inventory--in-stock');
      }

      if (textEl) textEl.innerHTML = message;

      if (barEl) {
        if (typeof qty === 'number' && qty > 0 && qty < threshold) {
          barEl.dataset.value = qty;
          barEl.hidden = false;
        } else {
          barEl.hidden = true;
        }
      }
    });
  }

  function applyBuyForms(root) {
    let capsApplied = false;

    root.querySelectorAll('[data-loc-inv-type="buy-form"]').forEach((container) => {
      const scope = container.closest('form, .product-form') || container;
      const variantInput = scope.querySelector('[data-loc-inv-variant], .product-variant-id');
      const submitBtn = scope.querySelector('[data-loc-inv-submit]');
      if (!variantInput) return;

      const variantId = variantInput.dataset.locInvVariant || variantInput.value;
      const qty = getQty(variantId);
      if (qty === undefined) return;

      const tracked = isTracked(variantInput);
      const min = Number(variantInput.dataset.locInvMin || container.dataset.locInvMin || 1);
      let soldOut = false;

      if (tracked && !allowsContinue(variantInput)) {
        soldOut = !inStock(qty) || (typeof qty === 'number' && qty < min);
      }

      if (!tracked || allowsContinue(variantInput) || soldOut) {
        setBuyFormExhausted(container, false);
      }

      variantInput.disabled = soldOut;
      if (submitBtn) {
        submitBtn.disabled = soldOut;
        const textEl = submitBtn.querySelector('.btn__text');
        if (textEl) {
          if (soldOut) {
            textEl.textContent = config.strings?.soldOut || textEl.textContent;
          } else if (textEl.dataset.defaultText) {
            textEl.textContent = textEl.dataset.defaultText;
          } else if (!textEl.dataset.defaultText) {
            textEl.dataset.defaultText = textEl.textContent;
          }
        }
      }

      if (applyBuyFormQuantityCap(scope, container, variantInput, qty)) {
        capsApplied = true;
      }
    });

    if (capsApplied) {
      const events = window.FoxTheme?.pubsub?.PUB_SUB_EVENTS;
      if (events?.quantityUpdate) {
        window.FoxTheme.pubsub.publish(events.quantityUpdate, undefined);
      }
    }
  }

  function applyBadges(root) {
    root.querySelectorAll('[data-loc-inv-type="badge"]').forEach((el) => {
      const variantId = el.dataset.locInvVariant;
      if (variantId) {
        const qty = getQty(variantId);
        if (qty === undefined) return;

        const tracked = isTracked(el);
        const soldOut = tracked && !allowsContinue(el) && !inStock(qty);
        el.querySelector('[data-loc-inv-sold-out-badge]')?.toggleAttribute('hidden', !soldOut);
        el.querySelector('[data-loc-inv-sale-badges]')?.toggleAttribute('hidden', soldOut);
        return;
      }

      const ids = (el.dataset.locInvVariants || '').split(',').map((id) => id.trim()).filter(Boolean);
      if (!ids.length) return;

      const soldOut = productCardSoldOutAtLocation(ids);
      if (soldOut === undefined) return;

      const soldOutBadge = el.querySelector('.f-badge--soldout[data-loc-inv-pcard-soldout]');
      if (soldOutBadge) soldOutBadge.hidden = !soldOut;
    });
  }

  function locationMaxForRow(row) {
    const variantId = row.dataset.locInvVariant;
    const qty = getQty(variantId);
    if (qty === undefined || !isTracked(row) || typeof qty !== 'number') return null;

    const ruleMax = row.dataset.locInvRuleMax ? Number(row.dataset.locInvRuleMax) : null;
    const cap = Math.max(0, qty);
    return ruleMax != null && !Number.isNaN(ruleMax) ? Math.min(ruleMax, cap) : cap;
  }

  function applyQuantityCapToInput(input, row) {
    const max = locationMaxForRow(row);
    if (max == null) return;

    input.max = max;
    input.dataset.max = String(max);
    // Order-list / cart line inputs are the cart qty itself — not PDP "add more" semantics.
    if (input.hasAttribute('data-order-list-input') || input.name === 'updates[]') {
      input.dataset.locInvAbsoluteMax = String(max);
    }
  }

  function applyQuantityCaps(root) {
    let capsApplied = false;
    root.querySelectorAll('[data-loc-inv-type="quantity-row"]').forEach((row) => {
      const variantId = row.dataset.locInvVariant;
      const qty = getQty(variantId);
      if (qty === undefined) return;

      const tracked = isTracked(row);
      const min = Number(row.dataset.locInvMin || 0);
      let soldOut = false;

      if (tracked && !allowsContinue(row)) {
        soldOut = !inStock(qty) || (typeof qty === 'number' && qty < min);
      }

      setSoldOutState(row, soldOut);

      const input = row.querySelector('[data-loc-inv-qty-input]');
      if (input && tracked && typeof qty === 'number') {
        applyQuantityCapToInput(input, row);
        capsApplied = true;
      }
    });

    if (capsApplied) {
      const events = window.FoxTheme?.pubsub?.PUB_SUB_EVENTS;
      if (events?.quantityUpdate) {
        window.FoxTheme.pubsub.publish(events.quantityUpdate, undefined);
      }
    }
  }

  function reapplyQuantityCaps(root = document) {
    if (!Object.keys(stock).length) return;
    applyQuantityCaps(root);
  }

  function cartLineCurrentQty(noticeEl) {
    const line = noticeEl.dataset.locInvLine;
    const cartScope = noticeEl.closest('cart-items') || document;

    if (line) {
      const input = cartScope.querySelector(`input[name="updates[]"][data-index="${line}"]`);
      if (input) {
        const value = parseInt(input.value, 10);
        if (!Number.isNaN(value)) return value;
      }
    }

    const fallback = parseInt(noticeEl.dataset.locInvCurrent || '', 10);
    return Number.isNaN(fallback) ? null : fallback;
  }

  function cartLineNoticeStatus(noticeEl) {
    const variantId = noticeEl.dataset.locInvVariant;
    const qty = getQty(variantId);
    if (qty === undefined) return { status: 'unknown' };

    // Untracked at this location, backorder allowed, or no per-location count: no limit.
    if (!isTracked(noticeEl) || allowsContinue(noticeEl) || typeof qty !== 'number') {
      return { status: 'ok' };
    }

    const ruleMax = noticeEl.dataset.locInvRuleMax ? Number(noticeEl.dataset.locInvRuleMax) : null;
    let max = Math.max(0, qty);
    if (ruleMax != null && !Number.isNaN(ruleMax)) max = Math.min(ruleMax, max);

    const min = Number(noticeEl.dataset.locInvMin || 0);
    const current = cartLineCurrentQty(noticeEl);
    if (current == null) return { status: 'unknown' };

    if (max <= 0 || max < min) return { status: 'soldout', max: 0, current };
    if (current > max) return { status: 'overstock', max, current };
    return { status: 'ok', max, current };
  }

  function setNoticeText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function setNoticeHtml(el, html) {
    if (el && el.innerHTML !== html) el.innerHTML = html;
  }

  // Flags cart lines that can't be fulfilled at the active location and reports
  // how many lines are blocked so checkout can be gated. Never mutates the cart.
  function applyCartLineNotices(root) {
    const strings = config.strings || {};
    let blockedCount = 0;

    root.querySelectorAll('[data-loc-inv-line-notice]').forEach((noticeEl) => {
      const textEl = noticeEl.querySelector('[data-loc-inv-line-notice-text]');
      const actionEl = noticeEl.querySelector('[data-loc-inv-line-action]');
      const { status, max } = cartLineNoticeStatus(noticeEl);

      if (status === 'ok' || status === 'unknown') {
        noticeEl.hidden = true;
        if (actionEl) actionEl.hidden = true;
        return;
      }

      blockedCount += 1;
      noticeEl.hidden = false;

      if (status === 'soldout') {
        setNoticeText(textEl, strings.lineUnavailable || '');
        if (actionEl) {
          actionEl.hidden = false;
          setNoticeText(actionEl, strings.lineRemove || '');
          actionEl.dataset.locInvActionQty = '0';
          actionEl.dataset.locInvLine = noticeEl.dataset.locInvLine || '';
        }
      } else {
        setNoticeHtml(textEl, (strings.lineOverstock || '').replace('[quantity]', max));
        if (actionEl) {
          actionEl.hidden = false;
          setNoticeText(actionEl, (strings.lineAdjust || '').replace('[quantity]', max));
          actionEl.dataset.locInvActionQty = String(max);
          actionEl.dataset.locInvLine = noticeEl.dataset.locInvLine || '';
        }
      }
    });

    return blockedCount;
  }

  function setCheckoutGate(blocked) {
    document
      .querySelectorAll('main-cart [name="checkout"], cart-drawer [name="checkout"]')
      .forEach((btn) => {
        btn.disabled = blocked;
        btn.classList.toggle('is-disabled', blocked);
      });

    // Accelerated/express checkout buttons bypass the cart form, so hide them too.
    document
      .querySelectorAll(
        'main-cart .additional-checkout-buttons, cart-drawer .additional-checkout-buttons'
      )
      .forEach((el) => el.toggleAttribute('hidden', blocked));

    document
      .querySelectorAll('[data-loc-inv-checkout-helper]')
      .forEach((el) => el.toggleAttribute('hidden', !blocked));
  }

  // Fail open: if per-location data is unavailable, never block a legitimate checkout.
  function applyCartGate(root = document) {
    if (document.documentElement.classList.contains('loc-inv--fallback')) {
      setCheckoutGate(false);
      return;
    }
    const blocked = applyCartLineNotices(root);
    setCheckoutGate(blocked > 0);
  }

  async function handleCartLineAction(event) {
    const button = event.target.closest('[data-loc-inv-line-action]');
    if (!button) return;

    event.preventDefault();
    const line = button.dataset.locInvLine;
    const targetQty = Number(button.dataset.locInvActionQty);
    if (!line || Number.isNaN(targetQty)) return;

    button.disabled = true;

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    try {
      const response = await fetch(`${window.FoxTheme.routes.cart_change_url}`, {
        ...window.FoxTheme.utils.fetchConfig(),
        body: JSON.stringify({ line: Number(line), quantity: targetQty, sections: sectionsToBundle }),
      });
      const parsedState = await response.json();
      const events = window.FoxTheme?.pubsub?.PUB_SUB_EVENTS;
      if (events?.cartUpdate) {
        window.FoxTheme.pubsub.publish(events.cartUpdate, { cart: parsedState, line, name: 'updates[]' });
      }
    } catch (error) {
      console.warn('[location-inventory] cart line action failed', error);
      button.disabled = false;
    }
  }

  async function clampCartOverstockIfNeeded() {
    if (cartOverstockClamped) return;
    if (!config.clampMainCartOverstock) return;

    const mainCart = document.querySelector('main-cart');
    if (!mainCart) return;

    const cartItems = mainCart.querySelector('cart-items');
    if (!cartItems) return;

    const rows = [...cartItems.querySelectorAll('[data-loc-inv-type="quantity-row"]')];
    if (!rows.length) return;

    const updates = {};
    let allResolved = true;

    for (const row of rows) {
      const input = row.querySelector('[data-loc-inv-qty-input]');
      if (!input) continue;

      const variantId = row.dataset.locInvVariant;
      const qty = getQty(variantId);
      if (qty === undefined) {
        allResolved = false;
        break;
      }

      if (!isTracked(row) || allowsContinue(row) || typeof qty !== 'number') continue;

      const max = locationMaxForRow(row);
      if (max == null) continue;

      const line = input.dataset.index;
      const current = parseInt(input.value, 10);
      if (line && !Number.isNaN(current) && current > max) {
        updates[line] = max;
      }
    }

    if (!allResolved) return;

    cartOverstockClamped = true;
    if (!Object.keys(updates).length) return;

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    try {
      const response = await fetch(`${window.FoxTheme.routes.cart_update_url}`, {
        ...window.FoxTheme.utils.fetchConfig(),
        body: JSON.stringify({ updates, sections: sectionsToBundle }),
      });
      const parsedState = await response.json();
      const events = window.FoxTheme?.pubsub?.PUB_SUB_EVENTS;
      if (events?.cartUpdate) {
        window.FoxTheme.pubsub.publish(events.cartUpdate, { cart: parsedState });
      }
    } catch (error) {
      console.warn('[location-inventory] cart clamp failed', error);
    }
  }

  function applyStockDisplays(root) {
    root.querySelectorAll('[data-loc-inv-type="stock-display"]').forEach((el) => {
      const variantId = el.dataset.locInvVariant;
      const qty = getQty(variantId);
      if (qty === undefined) return;

      const threshold = Number(el.dataset.locInvThreshold) || 100;
      const contentEl = el.querySelector('[data-loc-inv-stock-text]');
      if (!contentEl) return;

      if (!inStock(qty)) {
        contentEl.textContent = '-';
        return;
      }

      if (qty === null) {
        contentEl.textContent = '-';
        return;
      }

      if (typeof qty === 'number' && qty <= threshold) {
        contentEl.innerHTML = `Stock: <strong>${qty}u.</strong>`;
      } else if (typeof qty === 'number') {
        contentEl.innerHTML = `Stock: <span> +${threshold}u. </span>`;
      }
    });
  }

  function applySwatches(root) {
    root.querySelectorAll('[data-loc-inv-type="swatch"]').forEach((el) => {
      const ids = (el.dataset.locInvVariants || '').split(',').map((id) => id.trim()).filter(Boolean);
      if (!ids.length) return;

      const available = ids.some((id) => inStock(getQty(id)));
      el.classList.toggle('disabled', !available);
      const input = el.querySelector('input[type="radio"], a.swatch');
      if (input) input.classList.toggle('disabled', !available);
      if (input && input.tagName === 'INPUT') input.disabled = !available;
    });
  }

  function applyVariantOptions(root) {
    root.querySelectorAll('[data-loc-inv-type="variant-option"]').forEach((el) => {
      const variantId = el.dataset.locInvVariant;
      const qty = getQty(variantId);
      if (qty === undefined) return;

      const available = inStock(qty);
      el.classList.toggle('disabled', !available);
      const input = el.querySelector('input[type="radio"]');
      if (input) {
        input.disabled = !available;
        input.classList.toggle('disabled', !available);
        if (input.nextElementSibling) input.nextElementSibling.classList.toggle('disabled', !available);
      }
    });
  }

  function apply(root = document) {
    applyInventoryDisplay(root);
    applyBuyForms(root);
    applyBadges(root);
    applyQuantityCaps(root);
    applyStockDisplays(root);
    applySwatches(root);
    applyVariantOptions(root);
    applyCartGate(document);
  }

  async function hydrate(root = document) {
    const ok = await ensureStock(root);
    if (ok) {
      apply(root);
      await clampCartOverstockIfNeeded();
    }
  }

  function scheduleHydrate(root) {
    requestAnimationFrame(() => hydrate(root));
  }

  function initObserver() {
    if (observer) return;
    let timer;
    observer = new MutationObserver((mutations) => {
      const hasNew = mutations.some((m) => m.addedNodes.length > 0);
      if (!hasNew) return;
      clearTimeout(timer);
      timer = setTimeout(() => scheduleHydrate(document), 120);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function initBuyFormSubmitGuard() {
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.getAttribute('is') !== 'product-form') return;

        const buyForm = form.closest('[data-loc-inv-type="buy-form"]');
        if (!buyForm || buyForm.dataset.locInvExhausted !== 'true') return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const message =
          config.strings?.maxReached ||
          'You already have the maximum available quantity in your cart.';
        showBuyFormError(form, message);
      },
      true
    );
  }

  function initPubsub() {
    const events = window.FoxTheme?.pubsub?.PUB_SUB_EVENTS;
    if (!events) {
      setTimeout(initPubsub, 200);
      return;
    }
    window.FoxTheme.pubsub.subscribe(events.variantChange, (event) => {
      syncInventoryVariant(event?.data?.variant);
      scheduleHydrate(document);
    });
    window.FoxTheme.pubsub.subscribe(events.cartUpdate, () => {
      requestAnimationFrame(() => {
        reapplyQuantityCaps(document);
        applyBuyForms(document);
        applyCartGate(document);
      });
    });
    document.addEventListener('variant:changed', (event) => {
      syncInventoryVariant(event.detail?.variant);
      scheduleHydrate(document);
    });
    document.addEventListener('cart:updated', () => {
      requestAnimationFrame(() => {
        reapplyQuantityCaps(document);
        applyBuyForms(document);
        applyCartGate(document);
      });
    });
  }

  window.FoxTheme.locationInventory = {
    stock,
    inStock,
    getQty,
    refresh: hydrate,
    reapplyQuantityCaps,
  };

  document.documentElement.classList.add('loc-inv-enabled', 'loc-inv--pending');
  hydrate(document).then(() => {
    document.documentElement.classList.remove('loc-inv--pending');
  });
  initObserver();
  initPubsub();
  initBuyFormSubmitGuard();
  document.addEventListener('click', handleCartLineAction);
})();
